import { batch } from 'react-redux';
import { CustomerDisplayActions } from 'src/common/redux/reducers/customer-display-reducer';
import { CustomerDisplayScreen } from 'src/customer-display/enum/customer-display.enum';
import {
  beginPaymentSession,
  enablePayments,
  inEnablePayment,
  inPaymentSession,
  voidPayment,
} from 'src/devices/payment/pinpad-operations';
import { ThirdPartySessionData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import { TPSActionTypes } from '../../common/enumerations/tps-actions';
import { LocalStorage } from '../../common/helpers/local-storage';
import XLRLogger from '../../common/helpers/logger';
import { ITenderAdd } from '../../common/interfaces/dtos/IPreTenderAdd';
import { CCLPayload } from '../../common/interfaces/states/IPinpadState';
import { store } from '../../common/redux/configure-store';
import { ItemListActions } from '../../common/redux/reducers/item-list-reducer';
import { LoginActions } from '../../common/redux/reducers/login-reducer';
import { TenderingActions } from '../../common/redux/reducers/tendering-reducer';
import { TransactionActions } from '../../common/redux/reducers/transaction-reducer';
import { popupAsyncMessage } from '../../messages/business-logic/popup-message';
import {
  performTPSAction,
  TPSActions,
} from '../../tendering/business-logic/services/third-party-session';
import { swipeAhead } from './../../devices/payment/get-card-details';
import { applyCartResponse } from './common/apply-cart-response';
import { getPaymentOptions } from './get-payment-options';
import { getTransactionStatus } from './get-transaction-status';
import { tenderAdd } from './service/tender-add';
import { updateCartStatus } from './update-cart-status';

export const resumeTransaction = async (
  transactionId?: string,
  tillId?: string,
  storeId?: string,
): Promise<void> => {
  transactionId = transactionId || (store.getState().transaction.transactionId ?? '');
  tillId = tillId || (LocalStorage.tillId() ?? '');
  storeId = storeId || LocalStorage.storeId();

  if (tillId && transactionId) {
    const [data] = await Promise.all([
      getTransactionStatus(storeId, transactionId, tillId),
      getPaymentOptions(),
    ]);
    if (data.sequenceNumber) {
      store.dispatch(TransactionActions.setTransactionSequenceNumber(data.sequenceNumber));
      applyCartResponse(data);

      if (store.getState().pinPad.isPinpadConfigured) {
        const balanceDue = data.cartInfo?.totals?.balanceDue;
        await updateCartStatus();
        if (!inEnablePayment()) {
          await enablePayments();
        }
        if (balanceDue) {
          if (!inPaymentSession()) {
            await beginPaymentSession();
          }
          swipeAhead();
        }
      } else {
        await updateCartStatus();
      }
    }

    if (data.customer) {
      const points = data.customer.programBalances?.reduce(
        (state, item) => state + (item.earnedPoints || 0),
        0,
      );
      store.dispatch(
        TransactionActions.setCustomer({
          points: points || 0,
          consumerAccountNumber: data.customer.consumerAccountNumber,
          firstName: data.customer.firstName,
          lastName: data.customer.lastName,
          emailAddress: data.customer.emailAddress,
          mobilePhoneNumber: data.customer.mobilePhoneNumber,
          programBalances: data.customer.programBalances,
        }),
      );
    }
    if (data.thirdPartySession) {
      await recoverThirdPartySession(data.thirdPartySession).catch(showEFTFailureDialog);
    }
    store.dispatch(CustomerDisplayActions.setScreen(CustomerDisplayScreen.SalesScreen));
  } else {
    store.dispatch(CustomerDisplayActions.setScreen(CustomerDisplayScreen.IdleScreen));
  }
};

const recoverThirdPartySession = async (
  thirdPartySession: ThirdPartySessionData,
): Promise<void> => {
  const action = thirdPartySession.data.action;
  if (action === TPSActionTypes.EFTSwipePay) {
    await recoverFromCCLPayment(thirdPartySession);
  }
  //TODO: In future, add other recovery methods here
};

const recoverFromCCLPayment = async (thirdPartySession: ThirdPartySessionData): Promise<void> => {
  const payload = thirdPartySession.data.payload;
  if (payload) {
    const { paymentTranId, sessionId } = payload;
    if (paymentTranId && sessionId) {
      await voidPayment(paymentTranId, sessionId);
    }
    const lineTender = buildCCLLineTender(payload);
    lineTender.epsData = {
      ...lineTender.epsData,
      thirdPartySessionId: thirdPartySession.sessionId,
    };
    const transactionId = store.getState().transaction.transactionId;
    await tenderAdd(transactionId, lineTender, { printReceipt: true }).catch(err => {
      if (err.code !== 'EPSAuthorizationCanceled') {
        throw err;
      }
    });
    await performTPSAction(0, 0, TPSActions.Cancel, {
      sessionId: thirdPartySession.sessionId,
      transactionId,
      ...thirdPartySession.data,
    });
    store.dispatch(TenderingActions.setIsTendering(true));
  }
};

const buildCCLLineTender = ({
  cardDetails,
  tenderId,
  preTenderAmount,
  cashback,
  authCode,
  paymentTranId,
  receipt,
}: CCLPayload): ITenderAdd => ({
  epsData: {
    card: {
      cardType: cardDetails?.cardType,
      entryMethod: cardDetails?.entryMethod,
      maskedAccountNumber: cardDetails?.accountNumber,
      customerReceipt: [receipt ?? ''],
      merchantReceipt: [receipt ?? ''],
    },
    authorization: {
      referenceNumber: paymentTranId,
      authorizationCode: authCode ?? 'Decline',
    },
  },
  tender: {
    tenderId: tenderId,
    processedByEps: true,
    cancel: true,
    cancelReason: 'Recovery',
  },
  amount: {
    preTenderAmount: preTenderAmount,
    amount: 0,
    cashBack: cashback,
  },
});

const showEFTFailureDialog = async (err): Promise<void> => {
  XLRLogger.error({
    message: 'Failed to recover transaction',
    source: 'resume-transaction.ts',
    data: err,
  });

  await popupAsyncMessage({
    message: 'EFTRecoveryFailed',
    type: EmeraldDialogTypes.Error,
    subMessage: 'EFTRecoveryFailed_Desc',
  });

  batch(() => {
    store.dispatch(ItemListActions.removeAllRecords());
    store.dispatch(LoginActions.setLogin({ initialized: false }));
  });
};
